<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import configService from '@/services/config.service'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const isConfirmDialogVisible = ref(false)

definePage({
  meta: {
    name: 'config',
    subject: 'global.menu',
    action: 'config',
  },
})

const snackbar = useToast()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  key: [],
  type: [],
  unit_id: [],
})

const onError = error => {
  if (error.data){
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'Key',
    key: 'key',
  },
  {
    title: 'Type',
    key: 'type',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]


const {
  data: configData,
  isFetching,
  execute: fetchConfig,
} =  await useApi(createUrl('/configurations/configs', {
  query: {
    paged: 1,
    q: searchQuery,
    search: {
      unit_id: cookieRef("selectedUnit"),
    },
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const configs = computed(() => configData.value?.data)

const totalConfig = computed(() => configData.value?.meta?.total)


const configDeleteData = ref()

const deleteConfig = async (confirm, data) => {
  if(data?.id && confirm ){
    await configService.deleteConfig(data.id, () => {
      fetchConfig()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = data => {
  configDeleteData.value = data
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    configDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
          <VBtn
            v-if="$can('create', 'config.config_head')"
            :to="{ name: 'app-global-config-add' }"
            prepend-icon="tabler-plus"
          >
            Add New Config
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="configs"
        :items-length="totalConfig"
        :headers="headers"
        :loading="isFetching"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- User -->
        <template #item.key="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.key }}</span>
        </template>
        <template #item.type="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.type }}</span>
        </template>

    
        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn 
            v-if="$can('delete', 'configuration.config_head')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn v-if="$can('update', 'configuration.config_head')">
            <RouterLink
              :to="{ name: 'app-global-config-tab', params: { id: item.id } }"
              class="font-weight-medium text-link"
            >
              <VIcon icon="tabler-edit" />
            </RouterLink>
          </IconBtn>
        </template>
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>  
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Config Deleted."
      confirm-title="Deleted!"
      :data="configDeleteData"
      @confirm="deleteConfig"
    />
  </section>
</template>
